import type { User } from '@/types/user';
import type { Account } from '@/types/v2/account';
import type { Community } from '@/types/v2/community';
import type { CommunityRole } from '@/types/v2/community-role';
import type { User as UserV2 } from '@/types/v2/user';
import type { UserRole } from '@/types/v2/user-role';

export const hasRoles = (user: User | null, roles: string[]): boolean => {
  if (!user) return false;

  return roles.some((role) => user.abilities?.includes(role));
};

export const hasRolesExcluding = (user: User | null, roles: string[]): boolean => {
  if (!user) return false;

  return roles.every((role) => !user.abilities?.includes(role));
};

export const hasRolesExcludingV2 = (user: UserV2 | null, roles: string[]): boolean => {
  if (!user) return false;

  return roles.every((role) => {
    const abilities = Array.isArray(user.abilities) ? user.abilities : [];
    return !abilities.includes(role);
  });
};

export const hasRolesV2 = (user: UserV2 | null, roles: string[]): boolean => {
  if (!user) return false;

  return roles.some((role) => {
    const abilities = Array.isArray(user.abilities) ? user.abilities : [];
    return abilities.includes(role);
  });
};

export const hasCommunityRoles = (user: User | null, roles: string[]): boolean => {
  if (!user) return false;

  return roles.some((role) => `community:${user.default_community_role?.name}` === role);
};

export const hasCommunityRolesExcluding = (user: User | null, roles: string[]): boolean => {
  if (!user) return false;
  return roles.every((role) => `community:${user.default_community_role?.name}` !== role);
};

export const hasCommunityRolesV2 = (community: Community | null, roles: string[]): boolean => {
  if (!community) return false;

  const communityRoles = Array.isArray(community.roles) ? community.roles : [];
  return roles.some((role) => communityRoles.includes(role));
};

export const hasCommunityRolesExcludingV2 = (community: Community | null, roles: string[]): boolean => {
  if (!community) return false;

  const communityRoles = Array.isArray(community.roles) ? community.roles : [];
  return roles.every((role) => !communityRoles.includes(role));
};

export const hasAnyRoles = (user: User | null, roles: string[], communityRoles: string[]): boolean => {
  if (!user) return false;

  const hasGeneralRole = roles.some((role) => user.abilities?.includes(role));
  const hasCommunityRole = communityRoles.some(
    (communityRole) => `community:${user.default_community_role?.name}` === communityRole
  );

  return hasGeneralRole || hasCommunityRole;
};

export const hasAnyRolesV2 = (
  user: UserV2 | null,
  roles: string[],
  communityRoles: string[]
): boolean => {
  if (!user) return false;

  const userRoles = Array.isArray(user.roles) ? user.roles : [];
  const userCommunityRoles = Array.isArray(user.community_roles) ? user.community_roles : [];

  const hasGeneralRole = roles.some((role) => userRoles.includes(role as unknown as any));
  const hasCommunityRole = communityRoles.some((role) => userCommunityRoles.includes(role as unknown as any));

  return hasGeneralRole || hasCommunityRole;
};

export const hasAnyAccountRoles = (account: Account, roles: string[]): boolean => {
  if (!account) return false;

  const accountRoles = Array.isArray(account.roles) ? account.roles : [];
  return roles.some((role) => accountRoles.includes(role as unknown as any));
};

export const hasAnyRolesExcluding = (user: User | null, roles: string[], communityRoles: string[]): boolean => {
  if (!user) return false;

  const hasGeneralRole = roles.every((role) => !user.abilities?.includes(role));
  const hasCommunityRole = communityRoles.every(
    (communityRole) => `community:${user.default_community_role?.name}` !== communityRole
  );

  return hasGeneralRole && hasCommunityRole;
};

export const hasAnyRolesExcludingV2 = (
  user: UserV2 | null,
  roles: (UserRole & ('super_admin' | 'admin' | 'vendor' | 'user'))[],
  communityRoles: (CommunityRole & ('community_manager' | 'community_member'))[]
): boolean => {
  if (!user) return false;

  const userRoles = Array.isArray(user.roles) ? user.roles : [];
  const communityUserRoles = Array.isArray(user.community_roles) ? user.community_roles : [];

  const hasGeneralRole = roles.every((role) => !userRoles.includes(role));
  const hasCommunityRole = communityRoles.every((role) => !communityUserRoles.includes(role as unknown as any));

  return hasGeneralRole && hasCommunityRole;
};

export const hasAccountRoles = (account: Account, roles: string[]): boolean => {
  if (!account) return false;

  const accountRoles = Array.isArray(account.roles) ? account.roles : [];
  return roles.some((role) => accountRoles.includes(role as unknown as any));
};

